import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { CheckIcon, AlertIcon, ClockIcon, LockIcon, MailIcon, PhoneIcon, UserIcon, StoreIcon } from '../../components/Icons';
import { DocLink } from '../../components/DocModal';

export default function Login() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { login } = useAuth();
  const googleBtnRef = useRef(null);

  const destino = location.state?.from || null;

  const [mode, setMode]         = useState('login');
  const [metodo, setMetodo]     = useState('email');
  const [role, setRole]         = useState('cliente');
  const [nome, setNome]         = useState('');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [telefone, setTelefone] = useState('');
  const [loading, setLoading]   = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [erro, setErro]         = useState('');
  const [sucesso, setSucesso]   = useState('');
  const [aceitaTermos, setAceitaTermos]     = useState(false);
  const [aceitaContrato, setAceitaContrato] = useState(false);
  // Recuperação de password
  const [recovMode,    setRecovMode]    = useState(false); // true = modo recuperação
  const [recovStep,    setRecovStep]    = useState(1);     // 1=email, 2=código+nova pass
  const [recovEmail,   setRecovEmail]   = useState('');
  const [recovCodigo,  setRecovCodigo]  = useState('');
  const [recovPass,    setRecovPass]    = useState('');
  const [recovCodigoDev, setRecovCodigoDev] = useState('');

  const redireccionar = (u) => {
    if (destino) return navigate(destino, { replace: true });
    if (u.tipo === 'admin')           navigate('/admin',      { replace: true });
    else if (u.tipo === 'fornecedor') navigate('/fornecedor', { replace: true });
    else                              navigate('/cliente',    { replace: true });
  };

  // ── Inicializa Google Identity Services ─────────────────
  useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId || clientId === 'SEU_GOOGLE_CLIENT_ID_AQUI') return;

    // Guarda a referência do callback para ser acessível no window
    window.__kialugaGoogleCallback = handleGoogleCallback;

    const inicializar = () => {
      if (!window.google?.accounts?.id) return;
      try {
        window.google.accounts.id.initialize({
          client_id:              clientId,
          callback:               (resp) => window.__kialugaGoogleCallback(resp),
          auto_select:            false,
          cancel_on_tap_outside:  true,
          ux_mode:                'popup',
        });
        renderizarBotao();
      } catch (e) {
        console.warn('Google GSI inicialização falhou:', e.message);
      }
    };

    const renderizarBotao = () => {
      if (!googleBtnRef.current || !window.google?.accounts?.id) return;
      try {
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          theme:          'filled_black',
          size:           'large',
          width:          googleBtnRef.current.offsetWidth || 340,
          text:           mode === 'login' ? 'signin_with' : 'signup_with',
          locale:         'pt-PT',
          logo_alignment: 'left',
          shape:          'rectangular',
        });
      } catch (e) {
        console.warn('Google renderButton falhou:', e.message);
      }
    };

    // Se o SDK já carregou (cache de página), inicializa de imediato
    if (window.google?.accounts?.id) {
      inicializar();
    } else {
      // Aguarda o script async carregar via onload no elemento <script>
      const script = document.querySelector('script[src*="accounts.google.com/gsi/client"]');
      if (script) {
        const onLoad = () => inicializar();
        script.addEventListener('load', onLoad);
        // Timeout de segurança — 8 segundos
        const timer = setTimeout(inicializar, 8000);
        return () => { script.removeEventListener('load', onLoad); clearTimeout(timer); };
      }
    }
  // eslint-disable-next-line
  }, [mode]);

  // ── Callback do Google ───────────────────────────────────
  // Usa ref para ter sempre o role actualizado sem re-criar o callback
  const roleRef = useRef(role);
  useEffect(() => { roleRef.current = role; }, [role]);

  const handleGoogleCallback = async (response) => {
    if (!response?.credential) {
      setErro('Resposta Google inválida. Tenta novamente.');
      return;
    }
    setErro(''); setGoogleLoading(true);
    try {
      const res = await authAPI.loginGoogle(response.credential, roleRef.current);
      login(res.token, res.utilizador);
      setSucesso(`Bem-vindo, ${res.utilizador.nome}!`);
      setTimeout(() => redireccionar(res.utilizador), 600);
    } catch (err) {
      setErro(err.message || 'Erro ao entrar com Google. Verifica a tua ligação.');
    } finally {
      setGoogleLoading(false);
    }
  };

  // ── Fallback: botão Google manual ────────────────────────
  const handleGoogleManual = () => {
    if (window.google?.accounts?.id) {
      window.google.accounts.id.prompt();
    } else {
      setErro('O SDK do Google ainda está a carregar. Aguarda alguns segundos e tenta novamente.');
    }
  };

  // ── Email ────────────────────────────────────────────────
  const handleEmail = async (e) => {
    e.preventDefault();
    if (mode === 'register' && !aceitaTermos) {
      setErro('Deves aceitar os Termos e Condições e a Política de Privacidade para continuar.');
      return;
    }
    if (mode === 'register' && role === 'fornecedor' && !aceitaContrato) {
      setErro('Deves aceitar o Contrato de Fornecedor para continuar.');
      return;
    }
    setErro(''); setLoading(true);
    try {
      let res;
      if (mode === 'login') {
        res = await authAPI.login(email, password);
      } else {
        res = await authAPI.registar({ nome, email, password, tipo: role });
      }
      login(res.token, res.utilizador);
      setSucesso(`Bem-vindo${res.utilizador.nome ? ', ' + res.utilizador.nome : ''}!`);
      setTimeout(() => redireccionar(res.utilizador), 600);
    } catch (err) {
      setErro(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Telefone (login ou registo com password) ────────────
  const handleTelefone = async (e) => {
    e.preventDefault();
    if (mode === 'register' && !aceitaTermos) {
      setErro('Deves aceitar os Termos e Condições e a Política de Privacidade para continuar.');
      return;
    }
    if (mode === 'register' && role === 'fornecedor' && !aceitaContrato) {
      setErro('Deves aceitar o Contrato de Fornecedor para continuar.');
      return;
    }
    setErro(''); setLoading(true);
    try {
      let res;
      if (mode === 'login') {
        res = await authAPI.loginTelefone(telefone, password);
      } else {
        res = await authAPI.registarTelefone(nome, telefone, password, role);
      }
      login(res.token, res.utilizador);
      setSucesso(`Bem-vindo${res.utilizador.nome ? ', ' + res.utilizador.nome : ''}!`);
      setTimeout(() => redireccionar(res.utilizador), 600);
    } catch (err) {
      setErro(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Recuperar password passo 1 ──────────────────────────
  const handleRecuperarPass = async (e) => {
    e.preventDefault(); setErro(''); setLoading(true);
    try {
      const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const res  = await fetch(`${BASE}/auth/recuperar-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: recovEmail }),
      });
      const d = await res.json();
      if (d.sucesso) {
        setRecovStep(2);
        setSucesso(d.mensagem);
        if (d.codigo_dev) { setRecovCodigoDev(d.codigo_dev); setErro(`[DEV] Código: ${d.codigo_dev}`); }
      } else { setErro(d.mensagem || 'Erro ao recuperar password'); }
    } catch { setErro('Erro de ligação'); }
    finally { setLoading(false); }
  };

  // ── Redefinir password passo 2 ───────────────────────────
  const handleRedefinirPass = async (e) => {
    e.preventDefault(); setErro(''); setLoading(true);
    try {
      const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const res  = await fetch(`${BASE}/auth/redefinir-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: recovEmail, codigo: recovCodigo, nova_password: recovPass }),
      });
      const d = await res.json();
      if (d.sucesso) {
        setSucesso(d.mensagem);
        setTimeout(() => { setRecovMode(false); setRecovStep(1); setRecovEmail(''); setRecovCodigo(''); setRecovPass(''); setErro(''); setSucesso(''); }, 2000);
      } else { setErro(d.mensagem || 'Código inválido ou expirado'); }
    } catch { setErro('Erro de ligação'); }
    finally { setLoading(false); }
  };

  // ── Estilos ─────────────────────────────────────────────
  const inp = {
    width:'100%', padding:'10px 12px',
    background:'rgba(255,255,255,0.06)',
    border:'1px solid rgba(255,255,255,0.12)',
    borderRadius:'8px', fontSize:'13px', color:'#fff',
    fontFamily:'inherit', outline:'none',
    transition:'border 0.15s',
  };
  const lbl = {
    fontSize:'11px', color:'rgba(255,255,255,0.5)',
    textTransform:'uppercase', letterSpacing:'0.5px',
    display:'block', marginBottom:'5px',
  };
  const hasGoogleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID &&
                            process.env.REACT_APP_GOOGLE_CLIENT_ID !== 'SEU_GOOGLE_CLIENT_ID_AQUI';

  return (
    <div style={{
      minHeight:'100vh', background:'#060E1C',
      display:'flex', alignItems:'center', justifyContent:'center',
      padding:'20px', fontFamily:'Plus Jakarta Sans,sans-serif',
      backgroundImage:'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(26,74,138,0.3), transparent)',
    }}>
      {/* Modal recuperação de password */}
      {recovMode && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.7)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
          <div style={{background:'rgba(13,27,46,0.98)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'16px',padding:'28px',width:'100%',maxWidth:'380px'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'20px'}}>
              <div style={{fontSize:'16px',fontWeight:'700',color:'#fff'}}>Recuperar password</div>
              <button onClick={()=>{setRecovMode(false);setErro('');setSucesso('');}} style={{background:'none',border:'none',cursor:'pointer',color:'rgba(255,255,255,0.4)',fontSize:'18px'}}>×</button>
            </div>

            {recovStep === 1 && (
              <form onSubmit={handleRecuperarPass} style={{display:'flex',flexDirection:'column',gap:'14px'}}>
                <p style={{fontSize:'13px',color:'rgba(255,255,255,0.5)',lineHeight:'1.5',margin:0}}>
                  Introduz o teu email e receberás um código para redefinir a password.
                </p>
                <div>
                  <label style={lbl}>Email *</label>
                  <input type="email" required placeholder="email@exemplo.com"
                    value={recovEmail} onChange={e=>setRecovEmail(e.target.value)} style={inp}/>
                </div>
                {erro    && <Alerta tipo="erro"    msg={erro}/>}
                {sucesso && <Alerta tipo="sucesso" msg={sucesso}/>}
                <button type="submit" disabled={loading} style={{width:'100%',padding:'11px',background:'#F97316',color:'#fff',border:'none',borderRadius:'10px',fontSize:'13px',fontWeight:'600',cursor:loading?'not-allowed':'pointer',fontFamily:'inherit',opacity:loading?0.7:1}}>
                  {loading ? 'A enviar...' : 'Enviar código →'}
                </button>
              </form>
            )}

            {recovStep === 2 && (
              <form onSubmit={handleRedefinirPass} style={{display:'flex',flexDirection:'column',gap:'14px'}}>
                <p style={{fontSize:'13px',color:'rgba(255,255,255,0.5)',lineHeight:'1.5',margin:0}}>
                  Introduz o código enviado para <strong style={{color:'#F97316'}}>{recovEmail}</strong> e a tua nova password.
                </p>
                <div>
                  <label style={lbl}>Código de verificação *</label>
                  <input type="text" required maxLength={6} placeholder="000000"
                    value={recovCodigo} onChange={e=>setRecovCodigo(e.target.value.replace(/\D/g,''))}
                    style={{...inp,fontSize:'20px',letterSpacing:'8px',textAlign:'center',fontWeight:'700'}}/>
                </div>
                <div>
                  <label style={lbl}>Nova password *</label>
                  <input type="password" required minLength={6} placeholder="Mínimo 6 caracteres"
                    value={recovPass} onChange={e=>setRecovPass(e.target.value)} style={inp}/>
                </div>
                {erro    && <Alerta tipo="erro"    msg={erro}/>}
                {sucesso && <Alerta tipo="sucesso" msg={sucesso}/>}
                <button type="submit" disabled={loading} style={{width:'100%',padding:'11px',background:'#F97316',color:'#fff',border:'none',borderRadius:'10px',fontSize:'13px',fontWeight:'600',cursor:loading?'not-allowed':'pointer',fontFamily:'inherit',opacity:loading?0.7:1}}>
                  {loading ? 'A redefinir...' : 'Redefinir password →'}
                </button>
                <button type="button" onClick={()=>{setRecovStep(1);setErro('');setSucesso('');}} style={{background:'transparent',border:'none',color:'rgba(255,255,255,0.35)',fontSize:'12px',cursor:'pointer',fontFamily:'inherit',textAlign:'center'}}>
                  ← Alterar email
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Logo fixo no topo */}
      <div style={{position:'fixed',top:'20px',left:'24px',display:'flex',alignItems:'center',gap:'10px',cursor:'pointer',zIndex:10}}
        onClick={()=>navigate('/')}>
        <div style={{width:'32px',height:'32px',background:'#F97316',borderRadius:'8px',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:'800',fontSize:'17px',color:'#fff',fontFamily:'Syne,sans-serif'}}>K</div>
        <span style={{fontWeight:'700',fontSize:'15px',color:'#fff',fontFamily:'Syne,sans-serif'}}>KiAluga</span>
      </div>

      <div style={{width:'100%',maxWidth:'420px'}}>

        {/* Card principal */}
        <div style={{
          background:'rgba(13,27,46,0.95)',
          border:'1px solid rgba(255,255,255,0.08)',
          borderRadius:'20px', padding:'32px 28px',
          backdropFilter:'blur(20px)',
        }}>

          {/* Toggle Entrar / Criar conta */}
          <div style={{display:'flex',background:'rgba(255,255,255,0.05)',borderRadius:'10px',padding:'3px',marginBottom:'24px'}}>
            {[['login','Entrar'],['register','Criar conta']].map(([m,l])=>(
              <button key={m} onClick={()=>{setMode(m);setErro('');setSucesso('');setPassword('');}}
                style={{flex:1,padding:'8px',borderRadius:'8px',border:'none',cursor:'pointer',fontFamily:'inherit',
                  fontSize:'13px',fontWeight:'500',transition:'all 0.15s',
                  background:mode===m?'#F97316':'transparent',
                  color:mode===m?'#fff':'rgba(255,255,255,0.5)'}}>
                {l}
              </button>
            ))}
          </div>

          <h2 style={{fontFamily:'Syne,sans-serif',fontSize:'20px',fontWeight:'700',color:'#fff',marginBottom:'4px'}}>
            {mode==='login'?'Bem-vindo de volta!':'Cria a tua conta'}
          </h2>
          <p style={{fontSize:'12px',color:'rgba(255,255,255,0.4)',marginBottom:'22px',lineHeight:'1.5'}}>
            {destino ? <span style={{display:'inline-flex',alignItems:'center',gap:'5px'}}><LockIcon size={12} color='rgba(255,255,255,0.4)'/> Para continuar precisas de entrar na tua conta.</span> :
             mode==='login' ? 'Entra na tua conta KiAluga.' : 'Regista-te grátis em menos de 2 minutos.'}
          </p>

          {/* ── GOOGLE (botão principal, aparece primeiro) ── */}
          <div style={{marginBottom:'20px'}}>
            {hasGoogleClientId ? (
              <div>
                {/* Botão renderizado pelo Google SDK */}
                <div ref={googleBtnRef} style={{width:'100%',minHeight:'44px'}}></div>
                {googleLoading && (
                  <div style={{textAlign:'center',fontSize:'12px',color:'rgba(255,255,255,0.4)',marginTop:'8px'}}>
                    A verificar conta Google...
                  </div>
                )}
              </div>
            ) : (
              /* Botão manual enquanto Google não está configurado */
              <button onClick={handleGoogleManual} style={{
                width:'100%',padding:'11px',
                background:'rgba(255,255,255,0.07)',
                border:'1px solid rgba(255,255,255,0.12)',
                borderRadius:'10px',fontSize:'13px',fontWeight:'500',
                color:'#fff',cursor:'pointer',fontFamily:'inherit',
                display:'flex',alignItems:'center',justifyContent:'center',gap:'10px',
                transition:'all 0.15s',
              }}
                onMouseOver={e=>e.currentTarget.style.background='rgba(255,255,255,0.1)'}
                onMouseOut={e=>e.currentTarget.style.background='rgba(255,255,255,0.07)'}>
                <svg width="18" height="18" viewBox="0 0 48 48">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                </svg>
                {mode==='login' ? 'Entrar com Google' : 'Registar com Google'}
              </button>
            )}

            {/* Tipo de conta para Google — só no registo, pois novos utilizadores são criados na 1.ª vez */}
            {mode === 'register' && (
              <div style={{marginTop:'10px'}}>
                <div style={{fontSize:'11px',color:'rgba(255,255,255,0.3)',marginBottom:'6px',textAlign:'center'}}>
                  Vais registar-te como:
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px'}}>
                  {[['cliente',<UserIcon size={14}/> ,'Cliente'],['fornecedor',<StoreIcon size={14}/>,'Fornecedor']].map(([r,ic,t])=>(
                    <button key={r} onClick={()=>setRole(r)} style={{
                      padding:'8px',border:role===r?'1.5px solid #F97316':'1.5px solid rgba(255,255,255,0.08)',
                      borderRadius:'8px',cursor:'pointer',background:role===r?'rgba(249,115,22,0.1)':'transparent',
                      color:role===r?'#F97316':'rgba(255,255,255,0.5)',fontFamily:'inherit',
                      fontSize:'12px',fontWeight:'500',transition:'all 0.15s',
                      display:'flex',alignItems:'center',justifyContent:'center',gap:'6px',
                    }}>
                      {ic} {t}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Divisor */}
          <div style={{display:'flex',alignItems:'center',gap:'10px',marginBottom:'20px'}}>
            <div style={{flex:1,height:'1px',background:'rgba(255,255,255,0.07)'}}></div>
            <span style={{fontSize:'11px',color:'rgba(255,255,255,0.25)'}}>ou usa</span>
            <div style={{flex:1,height:'1px',background:'rgba(255,255,255,0.07)'}}></div>
          </div>

          {/* Tabs Email / Telefone */}
          <div style={{display:'flex',gap:'6px',marginBottom:'18px'}}>
            {[['email',<><MailIcon size={13}/> Email</>],['telefone',<><PhoneIcon size={13}/> Telefone</>]].map(([m,l])=>(
              <button key={m} onClick={()=>{setMetodo(m);setErro('');setSucesso('');setPassword('');}} style={{
                flex:1,padding:'7px',borderRadius:'8px',fontFamily:'inherit',cursor:'pointer',
                fontSize:'12px',fontWeight:'500',transition:'all 0.15s',
                border:metodo===m?'1px solid rgba(249,115,22,0.4)':'1px solid rgba(255,255,255,0.08)',
                background:metodo===m?'rgba(249,115,22,0.1)':'rgba(255,255,255,0.04)',
                color:metodo===m?'#F97316':'rgba(255,255,255,0.5)',
              }}>
                {l}
              </button>
            ))}
          </div>

          {/* Tipo de conta (email/telefone) */}
          {mode==='register' && (
            <div style={{marginBottom:'16px'}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px'}}>
                {[['cliente',<UserIcon size={20} color={role==='cliente'?'#F97316':'rgba(255,255,255,0.4)'}/> ,'Cliente','Alugar produtos'],['fornecedor',<StoreIcon size={20} color={role==='fornecedor'?'#F97316':'rgba(255,255,255,0.4)'}/>,'Fornecedor','Disponibilizar bens']].map(([r,ic,t,s])=>(
                  <div key={r} onClick={()=>setRole(r)} style={{
                    padding:'10px',borderRadius:'10px',cursor:'pointer',transition:'all 0.15s',
                    border:role===r?'1.5px solid #F97316':'1.5px solid rgba(255,255,255,0.08)',
                    background:role===r?'rgba(249,115,22,0.08)':'transparent',
                  }}>
                    <div style={{marginBottom:'3px'}}>{ic}</div>
                    <div style={{fontSize:'12px',fontWeight:'600',color:'#fff'}}>{t}</div>
                    <div style={{fontSize:'10px',color:'rgba(255,255,255,0.4)'}}>{s}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── FORM EMAIL ── */}
          {metodo==='email' && (
            <form onSubmit={handleEmail} style={{display:'flex',flexDirection:'column',gap:'12px'}}>
              {mode==='register' && (
                <div>
                  <label style={lbl}>Nome *</label>
                  <input type="text" required placeholder="O teu nome completo"
                    value={nome} onChange={e=>setNome(e.target.value)} style={inp}/>
                </div>
              )}
              <div>
                <label style={lbl}>Email *</label>
                <input type="email" required placeholder="email@exemplo.com"
                  value={email} onChange={e=>setEmail(e.target.value)} style={inp}/>
              </div>
              <div>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'5px'}}>
                  <label style={{...lbl,marginBottom:0}}>Password *</label>
                  {mode==='login' && (
                    <span style={{fontSize:'11px',color:'#F97316',cursor:'pointer'}}
                      onClick={()=>{setRecovMode(true);setRecovStep(1);setErro('');setSucesso('');setRecovEmail(email);}}>
                      Esqueceste?
                    </span>
                  )}
                </div>
                <input type="password" required placeholder="••••••••" minLength={mode==='register'?6:1}
                  value={password} onChange={e=>setPassword(e.target.value)} style={inp}/>
                {mode==='register' && password.length>0 && password.length<6 && (
                  <div style={{fontSize:'10px',color:'rgba(220,38,38,0.7)',marginTop:'3px'}}>Mínimo 6 caracteres</div>
                )}
              </div>

              {mode === 'register' && <ConsentCheckboxes role={role} aceitaTermos={aceitaTermos} setAceitaTermos={setAceitaTermos} aceitaContrato={aceitaContrato} setAceitaContrato={setAceitaContrato} />}

              {erro    && <Alerta tipo="erro"    msg={erro} />}
              {sucesso && <Alerta tipo="sucesso" msg={sucesso} />}

              <button type="submit" disabled={loading} style={{
                width:'100%',padding:'12px',background:'#F97316',color:'#fff',
                border:'none',borderRadius:'10px',fontSize:'14px',fontWeight:'600',
                cursor:loading?'not-allowed':'pointer',fontFamily:'inherit',
                opacity:loading?0.7:1,marginTop:'2px',transition:'all 0.15s',
              }}>
                {loading ? <span style={{display:'inline-flex',alignItems:'center',gap:'6px'}}><ClockIcon size={14}/> A processar...</span> : mode==='login' ? 'Entrar →' : 'Criar conta →'}
              </button>
            </form>
          )}

          {/* ── FORM TELEFONE ── */}
          {metodo==='telefone' && (
            <form onSubmit={handleTelefone} style={{display:'flex',flexDirection:'column',gap:'12px'}}>
              {mode==='register' && (
                <div>
                  <label style={lbl}>Nome *</label>
                  <input type="text" required placeholder="O teu nome completo"
                    value={nome} onChange={e=>setNome(e.target.value)} style={inp}/>
                </div>
              )}
              <div>
                <label style={lbl}>Telefone *</label>
                <input type="tel" required placeholder="+244 9XX XXX XXX"
                  value={telefone} onChange={e=>setTelefone(e.target.value)} style={inp}/>
              </div>
              <div>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'5px'}}>
                  <label style={{...lbl,marginBottom:0}}>Password *</label>
                  {mode==='login' && (
                    <span style={{fontSize:'11px',color:'#F97316',cursor:'pointer'}}
                      onClick={()=>{setRecovMode(true);setRecovStep(1);setErro('');setSucesso('');}}>
                      Esqueceste?
                    </span>
                  )}
                </div>
                <input type="password" required placeholder="••••••••" minLength={mode==='register'?6:1}
                  value={password} onChange={e=>setPassword(e.target.value)} style={inp}/>
                {mode==='register' && password.length>0 && password.length<6 && (
                  <div style={{fontSize:'10px',color:'rgba(220,38,38,0.7)',marginTop:'3px'}}>Mínimo 6 caracteres</div>
                )}
              </div>

              {mode === 'register' && <ConsentCheckboxes role={role} aceitaTermos={aceitaTermos} setAceitaTermos={setAceitaTermos} aceitaContrato={aceitaContrato} setAceitaContrato={setAceitaContrato} />}

              {erro    && <Alerta tipo="erro"    msg={erro} />}
              {sucesso && <Alerta tipo="sucesso" msg={sucesso} />}

              <button type="submit" disabled={loading} style={{
                width:'100%',padding:'12px',background:'#F97316',color:'#fff',
                border:'none',borderRadius:'10px',fontSize:'14px',fontWeight:'600',
                cursor:loading?'not-allowed':'pointer',fontFamily:'inherit',
                opacity:loading?0.7:1,marginTop:'2px',
              }}>
                {loading
                  ? <span style={{display:'inline-flex',alignItems:'center',gap:'6px'}}><ClockIcon size={14}/> A processar...</span>
                  : mode==='login' ? 'Entrar →' : 'Criar conta →'
                }
              </button>
            </form>
          )}

          {/* Link alternativo */}
          <p style={{textAlign:'center',fontSize:'12px',color:'rgba(255,255,255,0.3)',marginTop:'20px'}}>
            {mode==='login'
              ? <span>Sem conta? <span style={{color:'#F97316',cursor:'pointer',fontWeight:'500'}} onClick={()=>setMode('register')}>Regista-te grátis</span></span>
              : <span>Já tens conta? <span style={{color:'#F97316',cursor:'pointer',fontWeight:'500'}} onClick={()=>setMode('login')}>Entra aqui</span></span>
            }
          </p>
        </div>

        <p style={{textAlign:'center',fontSize:'11px',color:'rgba(255,255,255,0.18)',marginTop:'14px'}}>
          © 2025 KiAluga ·{' '}
          <span style={{cursor:'pointer',color:'rgba(255,255,255,0.25)'}} onClick={()=>navigate('/')}>← Voltar ao início</span>
        </p>
      </div>
    </div>
  );
}

// Checkboxes de consentimento legal
function ConsentCheckboxes({ role, aceitaTermos, setAceitaTermos, aceitaContrato, setAceitaContrato }) {
  const chk = { width:'15px', height:'15px', accentColor:'#F97316', cursor:'pointer', flexShrink:0 };
  const txt = { fontSize:'11px', color:'rgba(255,255,255,0.5)', lineHeight:'1.5' };
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'8px', padding:'10px 12px', background:'rgba(255,255,255,0.03)', borderRadius:'8px', border:'1px solid rgba(255,255,255,0.06)' }}>
      <label style={{ display:'flex', alignItems:'flex-start', gap:'8px', cursor:'pointer' }}>
        <input type="checkbox" checked={aceitaTermos} onChange={e => setAceitaTermos(e.target.checked)} style={chk}/>
        <span style={txt}>
          Li e aceito os <DocLink docKey="termos">Termos e Condições</DocLink>{' '}e a{' '}
          <DocLink docKey="privacidade">Política de Privacidade</DocLink> da KiAluga. *
        </span>
      </label>
      {role === 'fornecedor' && (
        <label style={{ display:'flex', alignItems:'flex-start', gap:'8px', cursor:'pointer' }}>
          <input type="checkbox" checked={aceitaContrato} onChange={e => setAceitaContrato(e.target.checked)} style={chk}/>
          <span style={txt}>
            Aceito o <DocLink docKey="contrato">Contrato de Fornecedor</DocLink> e a{' '}
            <DocLink docKey="kyc">Política de KYC</DocLink>. *
          </span>
        </label>
      )}
    </div>
  );
}

// Componente de alerta
function Alerta({ tipo, msg }) {
  const isErro = tipo === 'erro';
  return (
    <div style={{
      background: isErro?'rgba(220,38,38,0.1)':'rgba(22,163,74,0.1)',
      border: `1px solid ${isErro?'rgba(220,38,38,0.3)':'rgba(22,163,74,0.3)'}`,
      borderRadius:'8px', padding:'9px 12px',
      fontSize:'12px', color: isErro?'#fca5a5':'#86efac',
      lineHeight:'1.5',
    }}>
      <span style={{display:'inline-flex',alignItems:'center',gap:'5px',flexShrink:0}}>{isErro?<AlertIcon size={13} color='#fca5a5'/>:<CheckIcon size={13} color='#86efac'/>}</span>{msg}
    </div>
  );
}
